import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import {
  LoginContainer,
  Title,
  AdminLoginCard,
  LoaderOverlay,
  Spinner,
} from "./style";
import Fields from "@components/fields";
import Button from "@components/button";
import { Admin, Store } from "@apis/index";
import { Helper } from "@utils/index";
import Column from "@components/column";
import useNotification from "../../hooks/use-notification";
import theme from "@utils/theme";

const Login = ({ role }: { role?: string }) => {
  const navigate = useNavigate();
  const { fcmToken, requestPermission } = useNotification();

  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");

  const initialState = {
    username: "",
    password: "",
  };

  const handleSubmit = async (values: typeof initialState) => {
    try {
      setLoading(true);

      let token = fcmToken;
      // Ensure we have permission/token if possible, though hook tries on mount
      if (!token) {
        token = await requestPermission();
      }

      console.log("Device Token for Login:", token);

      const payload: any = {
        ...(role === "admin"
          ? { email: values.username }
          : { username: values.username }),
        password: values.password,
        ...(role === "store" && { role }),
        ...(token && { deviceToken: token }),
      };

      const loginWith = role === "admin" ? Admin : Store;
      const res = await loginWith.login({ data: payload });

      if (res?.twoFactorRequired) {
        setShow2FA(true);
        setTempToken(res.tempToken);
        toast.info("Please enter your 2FA code from Google Authenticator");
        return;
      }

      await Helper.saveUser(res);

      setTimeout(() => navigate(`/${res?.role}`), 500);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (code: string) => {
    try {
      setLoading(true);
      const loginWith = role === "admin" ? Admin : Store;
      const res = await loginWith.verifyLogin2FA({
        data: { tempToken, code },
      });

      console.log("Response from verifyLogin2FA:", res);
      await Helper.saveUser(res);
      setTimeout(() => navigate(`/${res?.role}`), 500);
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Invalid 2FA code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      {loading && (
        <LoaderOverlay>
          <Spinner />
          <p>Logging you in...</p>
        </LoaderOverlay>
      )}
      <AdminLoginCard>
        <Title>
          {show2FA
            ? "2-Step Verification"
            : role === "admin"
              ? "Admin"
              : "Store"}{" "}
          Login
        </Title>
        {show2FA ? (
          <Formik
            initialValues={{ code: "" }}
            onSubmit={(values) => {
              handle2FASubmit(values.code);
            }}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Column gap={15}>
                  <Fields.Input
                    name="code"
                    label="Authenticator Code"
                    placeholder="000000"
                  />
                  <Button
                    title={loading ? "Verifying..." : "Verify & Login"}
                    type="submit"
                    width={100}
                    height={45}
                    justify="center"
                    disabled={loading}
                  />
                  <p
                    style={{
                      textAlign: "center",
                      cursor: "pointer",
                      color: theme.orange.orange500,
                      marginTop: "10px",
                    }}
                    onClick={() => setShow2FA(false)}
                  >
                    Back to Login
                  </p>
                </Column>
              </Form>
            )}
          </Formik>
        ) : (
          <Formik initialValues={initialState} onSubmit={handleSubmit}>
            <Form>
              <Column>
                <Fields.Input
                  name="username"
                  label="Username"
                  placeholder="Enter your username"
                />
                <Fields.Password
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                />
                <Button
                  title={loading ? "Logging in..." : "Login"}
                  type="submit"
                  width={100}
                  height={45}
                  justify="center"
                  disabled={loading}
                />
              </Column>
            </Form>
          </Formik>
        )}
      </AdminLoginCard>
    </LoginContainer>
  );
};

export default Login;
